import { GroupedSearchResults, groupPagesByDomain } from "@/lib/utils";
import { useStore } from "@/store";
import { Page } from "@/schemas";
import { useMemo } from "react";

const getTimestampMillis = (
  timestamp: Date | { toMillis?: () => number } | null | undefined,
) => {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
  return 0;
};

export interface PagesResult {
  // Page State
  pages: Page[];
  draftPages: Page[];
  currentPage: Page | null;
  isLoading: boolean;
  error: string | null;

  // Page Actions
  setCurrentPageId: (pageId: string | null) => Promise<void>;
  addPage: (orgId: string, domainId: string, url: string) => Promise<string>;
  deletePage: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => Promise<void>;

  // Combined Action
  ensureDomainAndPageExists: (
    url: string,
  ) => Promise<{ domainId: string; pageId: string } | null>;

  // Grouped Pages
  groupedPagesByDomain: GroupedSearchResults[];
}

/**
 * Custom hook to access and manage page-related data and operations
 */
export const usePages = (): PagesResult => {
  const {
    // Page state
    pages,
    draftPages,
    currentPage,
    isLoadingPages,
    pageError,

    // Page actions
    setCurrentPageId,
    addPage,
    deletePage,

    // Combined action
    ensureDomainAndPageExists,

    // For grouped pages calculation
    domains,
  } = useStore();

  // Sort all pages by creation date (descending)
  const sortedPages = useMemo(
    () =>
      [...pages].sort((a, b) => {
        const timeA = getTimestampMillis(a.created_at);
        const timeB = getTimestampMillis(b.created_at);
        return timeB - timeA;
      }),
    [pages],
  );

  // Calculate grouped pages — all pages including drafts (drafts matched by domainId)
  const groupedPagesByDomain = useMemo(
    () => groupPagesByDomain(domains, sortedPages),
    [domains, sortedPages],
  );

  return {
    // Page state
    pages: sortedPages,
    draftPages,
    currentPage,
    isLoading: isLoadingPages,
    error: pageError,

    // Page actions
    setCurrentPageId,
    addPage,
    deletePage,

    // Combined action
    ensureDomainAndPageExists,

    // Grouped Pages
    groupedPagesByDomain,
  };
};
